import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEO } from "@/components/SEO";
import { SEOBreadcrumb } from "@/components/SEOBreadcrumb";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { toast } from "@/hooks/use-toast";
import { MessageSquare, MapPin, Facebook, Instagram, Linkedin, Youtube } from "lucide-react";
import { z } from "zod";
import { sanitizeName, sanitizeEmail, sanitizeText } from "@/lib/sanitize";
import { supabase } from "@/integrations/supabase/client";

const contactSchema = z.object({
  name: z.string().trim().min(1, "Il nome è obbligatorio").max(100),
  email: z.string().trim().email("Email non valida").max(255),
  subject: z.string().trim().min(1, "L'oggetto è obbligatorio").max(200),
  message: z.string().trim().min(10, "Il messaggio deve essere di almeno 10 caratteri").max(2000)
});

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Validazione
      contactSchema.parse(formData);
      
      setIsSubmitting(true);
      
      // Sanitize data before submission
      const sanitizedData = {
        name: sanitizeName(formData.name),
        email: sanitizeEmail(formData.email),
        subject: sanitizeText(formData.subject),
        message: sanitizeText(formData.message),
      };
      
      // Call edge function to send email
      const { data, error } = await supabase.functions.invoke('send-contact-form', {
        body: sanitizedData
      });

      if (error) {
        throw new Error(error.message || "Errore nell'invio del messaggio");
      }
      
      toast({ title: "Messaggio inviato con successo! Ti risponderemo presto.", variant: "success" });
      
      // Reset form
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: ""
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({ title: error.errors[0].message, variant: "error" });
      } else {
        toast({ title: "Errore durante l'invio. Riprova più tardi.", variant: "error" });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Contattaci - Inclusia"
        description="Hai domande? Contatta il team di Inclusia. Siamo qui per aiutarti."
      />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-surface py-20 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <SEOBreadcrumb items={[{ label: t('footer.contact') }]} />
            <h1 className="font-heading text-4xl md:text-6xl font-bold mb-6 text-dark">
              {t("footer.contact")}
            </h1>
            <p className="text-xl text-gray-700">
              Siamo qui per aiutarti. Inviaci un messaggio e ti risponderemo il prima possibile.
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-6">
              <BrutalCard>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary border-[3px] border-dark flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-6 h-6 text-dark" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold mb-2">Scrivici</h3>
                    <p className="text-gray-700">Compila il form per contattarci</p>
                    <p className="text-sm text-gray-600 mt-2">
                      Tempo di risposta: entro 24 ore
                    </p>
                  </div>
                </div>
              </BrutalCard>

              <BrutalCard>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary border-[3px] border-dark flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-dark" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold mb-2">Sede</h3>
                    <p className="text-gray-700">Milano, Italia</p>
                  </div>
                </div>
              </BrutalCard>

              <BrutalCard>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary border-[3px] border-dark flex items-center justify-center flex-shrink-0">
                    <Instagram className="w-6 h-6 text-dark" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold mb-2">Social</h3>
                    <p className="text-gray-700">
                      Seguici sui social media per aggiornamenti e novità
                    </p>
                    <div className="flex gap-4 mt-3">
                      <a 
                        href="https://www.linkedin.com/company/110432609/admin/dashboard/" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-primary hover:underline flex items-center gap-1"
                      >
                        <Linkedin className="w-4 h-4" />
                        LinkedIn
                      </a>
                      <a 
                        href="https://www.facebook.com/inclusiapro/" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-primary hover:underline flex items-center gap-1"
                      >
                        <Facebook className="w-4 h-4" />
                        Facebook
                      </a>
                      <a 
                        href="https://www.instagram.com/inclusiapro/" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-primary hover:underline flex items-center gap-1"
                      >
                        <Instagram className="w-4 h-4" />
                        Instagram
                      </a>
                      <a 
                        href="https://www.youtube.com/@inclusiapro" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-primary hover:underline flex items-center gap-1"
                      >
                        <Youtube className="w-4 h-4" />
                        YouTube
                      </a>
                    </div>
                  </div>
                </div>
              </BrutalCard>
            </div>

            {/* Contact Form */}
            <BrutalCard>
              <h2 className="font-heading text-2xl font-bold mb-6">
                Invia un messaggio
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block font-heading font-bold mb-2">
                    Nome *
                  </label>
                  <BrutalInput
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    maxLength={100}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block font-heading font-bold mb-2">
                    Email *
                  </label>
                  <BrutalInput
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    maxLength={255}
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block font-heading font-bold mb-2">
                    Oggetto *
                  </label>
                  <BrutalInput
                    id="subject"
                    name="subject"
                    type="text"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    maxLength={200}
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block font-heading font-bold mb-2">
                    Messaggio *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={2000}
                    rows={6}
                    className="w-full px-4 py-3 border-[3px] border-dark focus:outline-none focus:ring-4 focus:ring-primary font-body"
                  />
                  <p className="text-sm text-gray-600 mt-1">
                    {formData.message.length}/2000 caratteri
                  </p>
                </div>

                <BrutalButton
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full"
                >
                  {isSubmitting ? "Invio in corso..." : "Invia messaggio"}
                </BrutalButton>
              </form>
            </BrutalCard>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
